import { useEffect, useRef, useState } from "react";
import { BaseItem } from "../../interfaces/MediaAndContent";
import BoxLogos from "../Cards/BoxLogos";

interface InfiniteScrollProps {
    items: BaseItem[];
    itemWidth?: number;
    gap?: number;
}

const InfiniteHorizontalAutoScroll = ({
    items,
    itemWidth = 180,
    gap = 32,
}: InfiniteScrollProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const animatedRef = useRef<HTMLDivElement>(null);

    const [displayedItems, setDisplayedItems] = useState<BaseItem[]>([]);
    const [isTouching, setIsTouching] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    // Prepare repeated items for infinite scroll
    useEffect(() => {
        if (!items || items.length === 0 || !scrollRef.current) return;

        const containerWidth = scrollRef.current.clientWidth;
        const minItems = Math.ceil(containerWidth / (itemWidth + gap));

        const newItems = [];
        while (newItems.length < minItems * 15) {
            newItems.push(...items);
        }

        setDisplayedItems(newItems);
    }, [items, itemWidth, gap]);

    // Detect mobile devices for animation timing
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.matchMedia("(pointer: coarse)").matches);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Play/pause animation helpers
    const pauseAnimation = () => {
        if (animatedRef.current) animatedRef.current.style.animationPlayState = "paused";
    };

    const resumeAnimation = () => {
        if (animatedRef.current) animatedRef.current.style.animationPlayState = "running";
    };

    // Handle touch start (begin swipe)
    const handleTouchStart = (e: React.TouchEvent) => {
        setIsTouching(true);
        if (!scrollRef.current) return;
        isDragging.current = true;
        startX.current = e.touches[0].pageX;
        scrollLeft.current = scrollRef.current.scrollLeft;
        pauseAnimation();
    };

    // Handle touch end (finish swipe)
    const handleTouchEnd = () => {
        isDragging.current = false;
        setIsTouching(false);
        resumeAnimation();
    };

    // Native DOM listener for touchmove to fix Chrome on iOS
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        // Manual touch move handler (bypasses React synthetic event system)
        const handleManualTouchMove = (e: TouchEvent) => {
            // Required for Chrome on iOS: React's onTouchMove doesn't work as expected
            if (!isDragging.current) return;
            e.preventDefault(); //Stops native scroll interference
            const x = e.touches[0].pageX;
            const walk = x - startX.current;
            el.scrollLeft = scrollLeft.current - walk;
        };

        // Add touchmove listener with passive: false to ensure it works on WebKit
        el.addEventListener("touchmove", handleManualTouchMove, { passive: false });

        // Cleanup on unmount
        return () => {
            el.removeEventListener("touchmove", handleManualTouchMove);
        };
    }, []);

    // Handle mouse drag
    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        startX.current = e.pageX;
        scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
        pauseAnimation();
    };

    const handleMouseUpOrLeave = () => {
        isDragging.current = false;
        resumeAnimation();
    };

    return (
        <div
            ref={scrollRef}
            className={`relative w-full mt-6 scroll-smooth snap-x snap-mandatory touch-pan-x overflow-y-hidden ios-scroll ${isTouching ? "overflow-x-scroll" : "overflow-x-hidden"
                }`}
            style={{
                WebkitOverflowScrolling: "touch", // Enables momentum scrolling on iOS
                touchAction: "pan-x",              // Allows horizontal swipe gestures
            }}
            onMouseEnter={pauseAnimation}
            onMouseLeave={resumeAnimation}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUpOrLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div
                ref={animatedRef}
                className="flex w-max py-1 gap-8"
                style={{
                    animationName: "scroll",
                    animationDuration: `${isMobile ? "60s" : "150s"}`,
                    animationTimingFunction: "linear",
                    animationIterationCount: "infinite",
                    animationPlayState: "running",
                }}
            >
                <BoxLogos displayedItems={displayedItems} />
            </div>
        </div>
    );
};

export default InfiniteHorizontalAutoScroll; 